<template>
  <v-card class="donation-flow" style="padding: 40px; border-radius: 16px;">
    <v-card-title
      class="text-center mx-auto d-flex justify-space-between mb-8"
      data-cy="detailsview-title"
      style="padding: 0; line-height: 1;"
    >
      <span class="donation-flow__step-title">
        {{ isContribution ? t('donationGuide.certificateDetails.donationRecipient') : t('donationDetails.title') }}
      </span>
    </v-card-title>
    <v-card-text class="pa-0">
      <v-form ref="form" v-model="valid" lazy-validation @submit.prevent>
        <donation-details-form-component
          v-if="isMemoryGift"
          v-model:value="donationDetailsAny"
          data-cy="detailsview-donation-form"
          :form="form"
        />
        <basic-donation-details-form-component
          v-else-if="isContribution"
          v-model:value="donationDetailsAny"
          data-cy="detailsview-donation-details-form"
          @remove-step="$emit('removeStep')"
          @previous-step="$emit('previousStep')"
          @next-step="next"
        />
      </v-form>
      <v-alert v-model="tooManyAttributeRows" dense text type="error">
        <h5>{{ t('validation.tooManyRowsTitle') }}</h5>
        {{ t('validation.tooManyRows', { count: textAreaValidation.maxTotalRowCount }) }}
      </v-alert>
    </v-card-text>
    <donation-flow-buttons
      previous-id="donationDetailsViewBackButton"
      next-id="donationDetailsViewNextButton"
      previous-data-cy="detailsview-back-button"
      next-data-cy="detailsview-next-button"
      :next-disabled="tooManyAttributeRows"
      @previous-step="previous"
      @next-step="next"
    />
  </v-card>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch } from 'vue';
import { useLabel } from '@/composables/useLabel';
import { RequestSession } from '@/interfaces/requestSession';
import { Donation } from '@/interfaces/donation/donation';
import { AttributeValue } from '@/interfaces/donation/attributeValue';
import DonationDetailsFormComponent from '@/components/donation/DonationDetailsFormComponent.vue';
import BasicDonationDetailsFormComponent from '@/components/donation/BasicDonationDetailsFormComponent.vue';
import DonationFlowButtons from '@/components/DonationFlowButtons.vue';
import { isMemoryGift, isContribution } from '@/classes/DonationType';
import { AttributeType } from '@/enums/attributeType';
import { cleanTextFromTrailingEmptyRows, countRowsInTextArea } from '@/utils';
import matomo from '@/services/matomoService';
import store from '@/store';
import { scrollToFirstInvalid } from '@/utils/formHelpers';

export default defineComponent({
  components: {
    DonationDetailsFormComponent,
    BasicDonationDetailsFormComponent,
    DonationFlowButtons,
  },
  // validation rules are provided by composable useValidationRules when needed
  props: {
    value: {
      type: Object,
      default: null,
    },
    showPrev: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['previousStep', 'nextStep', 'removeStep'],
  setup(props, { emit }) {
    const { t } = useLabel();
    const form = ref(null);
    const exampleMessages = ref(t('exampleMessages'));
    const lazy = ref(true);
    const valid = ref(true);
    const dialog = ref(false);
    const donationDetails = ref({
      attributeValues: [
        {
          value: '',
          attributeId: AttributeType.SUBJECT_NAME,
        },
        {
          value: '',
          attributeId: AttributeType.GREETING,
        },
        {
          value: '',
          attributeId: AttributeType.DONOR_NAME,
        },
      ] as Array<AttributeValue>,
      donationCause: null,
      donationImageId: null,
      showCondolenceOnMemorialPage: null,
    });
    const showCause = ref(false);
    const images = ref([]);
    const categories = ref([]);
    const noSenderName = ref(false);
    const textAreaValidation = ref({
      message: {
        maxRowSize: 130,
      },
      donorNames: {
        maxRowSize: 125,
      },
      maxTotalRowCount: 17,
      valid: true,
    });
    const attributes = ref({
      subjectName: AttributeType.SUBJECT_NAME,
      greeting: AttributeType.GREETING,
      donorName: AttributeType.DONOR_NAME,
    });

    const requestSession = computed((): RequestSession | null => store.state.requestSession);
    const donation = computed((): Donation | null => store.state.donation);
    const isMemoryGiftComputed = computed((): boolean =>
      donation.value ? isMemoryGift(donation.value.donationType.type) : false
    );
    const isContributionComputed = computed((): boolean =>
      donation.value ? isContribution(donation.value.donationType.type) : false
    );

    // Template-safe wrapper so templates can bind via .sync
    const donationDetailsAny = computed({
      get(): {
        attributeValues: AttributeValue[];
        donationCause;
        donationImageId: number | null;
        showCondolenceOnMemorialPage: boolean | null;
      } {
        return donationDetails.value;
      },
      set(val: {
        attributeValues: AttributeValue[];
        donationCause;
        donationImageId: number | null;
        showCondolenceOnMemorialPage: boolean | null;
      }) {
        donationDetails.value = val;
      },
    });

    const tooManyAttributeRows = computed(
      () => donationDetails.value && isMemoryGiftComputed.value && !hasValidAttributeRowCount()
    );

    const next = async () => {
      // Trigger form validation to show all error messages (Vuetify 3 returns a Promise)
      const { valid: formValid } = await form.value.validate();

      // Manual validation for fields not covered by Vuetify rules
      let isValid = formValid;

      if (isMemoryGiftComputed.value) {
        // For memory gifts, recipient name and donation cause are required
        const recipientName = getAttributeValueById(attributes.value.subjectName);
        if (!recipientName || !recipientName.trim()) {
          isValid = false;
        }
        if (!donationDetails.value.donationCause) {
          isValid = false;
        }
      } else if (isContributionComputed.value) {
        // For contributions, donation cause is required
        if (!donationDetails.value.donationCause) {
          isValid = false;
        }
      }

      if (!isValid) {
        // Find and scroll to the first invalid control
        try {
          scrollToFirstInvalid(form.value, null);
        } catch {
          // no-op
        }
        return;
      }

      const donationVal: Donation = donation.value!;
      if (isMemoryGiftComputed.value) {
        cleanAttributeTexts();
        donationVal.attributeValues = donationDetails.value.attributeValues as AttributeValue[];
        donationVal.donationImageId = donationDetails.value.donationImageId;
      }

      if (donationDetails.value.donationCause) {
        const { organizationGroup: _o1, pingPaymentsMerchant: _o2, pennybridgeCause: _o3, pennybridgeId: _o4, entityType: _o5, createdAt: _o6, updatedAt: _o7, ...causeWithoutOrg } = donationDetails.value.donationCause as any;
        donationVal.donationCause = causeWithoutOrg;
      } else {
        donationVal.donationCause = donationDetails.value.donationCause;
      }
      donationVal.showCondolenceOnMemorialPage = donationDetails.value.showCondolenceOnMemorialPage;
      store.commit('updateDonation', donationVal);
      matomo.trackEvent(
        'donation_details',
        'proceeded_to_checkout',
        'donations_details_proceeded_to_checkout'
      );
      emit('nextStep');
    };

    const previous = () => {
      matomo.trackEvent(
        'donation_details',
        'previous_clicked',
        'donations_details_previous_clicked'
      );
      emit('previousStep');
    };

    const hasValidAttributeRowCount = () => {
      const message = getAttributeValueById(AttributeType.GREETING);
      const donorNames = getAttributeValueById(AttributeType.DONOR_NAME);

      const totalRows =
        countRowsInTextArea(message, textAreaValidation.value.message.maxRowSize) +
        countRowsInTextArea(donorNames, textAreaValidation.value.donorNames.maxRowSize);
      return totalRows <= textAreaValidation.value.maxTotalRowCount;
    };

    const cleanAttributeTexts = () => {
      const greetingAttribute = cleanTextFromTrailingEmptyRows(
        getAttributeValueById(attributes.value.greeting)
      );
      setAttributeValueById(attributes.value.greeting, greetingAttribute, false);

      const namesAttribute = cleanTextFromTrailingEmptyRows(
        getAttributeValueById(attributes.value.donorName)
      );
      setAttributeValueById(attributes.value.donorName, namesAttribute, false);
    };

    const getAttributeValueById = (id: number): string => {
      const attributeValue = donationDetails.value.attributeValues.find(
        (a) => a.attributeId === id
      );
      return attributeValue ? attributeValue.value : '';
    };

    const setAttributeValueById = (id: number, value: string, concat: boolean) => {
      const attributeValue = donationDetails.value.attributeValues.find(
        (a) => a.attributeId === id
      );

      if (attributeValue) {
        if (concat) {
          attributeValue.value += value;
        } else {
          attributeValue.value = value;
        }
      }
    };

    watch(donation, () => {
      donationDetails.value = {
        attributeValues: [
          { value: '', attributeId: AttributeType.SUBJECT_NAME },
          { value: '', attributeId: AttributeType.GREETING },
          { value: '', attributeId: AttributeType.DONOR_NAME },
        ] as Array<AttributeValue>,
        donationCause: null,
        donationImageId: null,
        showCondolenceOnMemorialPage: null,
      };
    });

    return {
      exampleMessages,
      t,
      lazy,
      valid,
      dialog,
      donationDetails,
      donationDetailsAny,
      showCause,
      images,
      categories,
      noSenderName,
      textAreaValidation,
      attributes,
      requestSession,
      donation,
      isMemoryGift: isMemoryGiftComputed,
      isContribution: isContributionComputed,
      tooManyAttributeRows,
      next,
      previous,
      hasValidAttributeRowCount,
      cleanAttributeTexts,
      getAttributeValueById,
      form,
      setAttributeValueById,
    };
  },
});
</script>
